import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { debugLog } from '../../utils/debugLog';
import {
    Box,
    Text,
    Alert,
    TextInput,
    Button,
    ActionIcon,
    Paper,
    MultiSelect,
    Select,
    Checkbox,
    Tooltip,
    Group,
    Badge,
    Anchor,
    Tabs,
    Grid,
    Stack,
} from '@mantine/core';
import {
    IconSearch,
    IconAdjustments,
    IconX,
    IconMaximize,
    IconMinimize,
    IconRefresh,
    IconPlus,
    IconAlertTriangle,
    IconInfoCircle,
} from '@tabler/icons-react';
import { GenericTable, type TableColumn, type TableAction, type SortState, type ListResponse } from './GenericTable';
import { CommonPagination } from './Pagination';
import { Phil } from './Phil';
import { LoadingState } from './LoadingState';
import { getPluralizedLabel, getSingularLabel } from '../../utils/pluralizationUtils';
import { getSearchInAttributesForSchema } from '../../utils/searchInOptions';
import { useSchemas } from '../../store/schemaHooks';
import { writeTableDeepLinkStateToHash } from '../../utils/tableDeepLinkUtils';
import { detectAutoSearchKind } from '../../utils/simpleSearchUtils';
import { buildScimFilterEditorCompletions } from '../../utils/scimFilterEditorCompletions';
import { ScimAceEditor } from './ScimAceEditor';

function filterSearchInByAllowed(
    attributes: string[],
    allowedSet: Set<string> | null
): string[] {
    if (allowedSet === null) return attributes;
    if (allowedSet.size === 0) return [];
    return attributes.filter((a) => allowedSet!.has((a || '').toLowerCase()));
}

function prioritizePreferredSearchAttributes(attributes: string[]): string[] {
    if (!attributes || attributes.length === 0) return [];
    const preferredOrder = ['id', 'displayname', 'userprincipalname'];
    const seen = new Set<string>();
    const preferred: string[] = [];
    const others: string[] = [];

    for (const pref of preferredOrder) {
        const match = attributes.find((a) => (a || '').toLowerCase() === pref);
        if (match) {
            const key = match.toLowerCase();
            if (!seen.has(key)) {
                seen.add(key);
                preferred.push(match);
            }
        }
    }

    for (const attr of attributes) {
        const key = (attr || '').toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        others.push(attr);
    }

    return [...preferred, ...others];
}

function isHighPrioritySearchAttribute(attribute: string): boolean {
    const key = (attribute || '').toLowerCase();
    return key === 'id' || key === 'displayname' || key === 'userprincipalname';
}

function extractScoreRange<T>(data: T[]): { min: number; max: number } | null {
    if (!data || data.length === 0) return null;
    const scores: number[] = [];
    const findScores = (obj: any): void => {
        if (!obj || typeof obj !== 'object') return;
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                const value = obj[key];
                if (typeof value === 'number' && key.toLowerCase().includes('score')) {
                    scores.push(value);
                }
                if (value && typeof value === 'object' && !Array.isArray(value)) {
                    findScores(value);
                }
            }
        }
    };
    data.forEach(item => findScores(item));
    if (scores.length === 0) return null;
    return { min: Math.min(...scores), max: Math.max(...scores) };
}


export interface GenericSearchProps<T> {
    entityType: string;
    authResolved?: boolean;
    data: T[];
    isLoading: boolean;
    error?: string | null;
    currentPage: number;
    totalResults: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
    columns: TableColumn<T>[];
    getRowKey: (item: T) => string;
    onRowClick?: (item: T) => void;
    emptyMessage?: string;
    selectedEntityId?: string | null;
    deepLinkEntityType?: string | null;
    onEntitySelect?: (entity: T) => void;
    entityService?: {
        getEntityById?: (id: string) => Promise<T>;
    };
    onSearch?: (query: string, searchAttributes?: string[], expressionDialectOmitted?: boolean | null, searchType?: 'simple' | 'simpleCustom') => void;
    onAdvancedSearch?: (filters: any) => void;
    initialSearchQuery?: string;
    /**
     * Legacy: single draft buffer for search input. Prefer per-mode handlers below.
     * When provided, GenericSearch will treat the input as controlled.
     */
    onSearchQueryChange?: (query: string) => void;
    /** Draft buffer for regular simple search input (TextInput mode). */
    simpleSearchQuery?: string;
    /** Draft buffer for advanced expression-tab query. */
    expressionSearchQuery?: string;
    /** Draft buffer for advanced freetext-tab query. */
    freetextSearchQuery?: string;
    /** Draft buffer for custom filter (manual SCIM editor). */
    customSearchQuery?: string;
    /** Controlled change handler for regular simple search buffer. */
    onSimpleSearchQueryChange?: (query: string) => void;
    /** Controlled change handler for advanced expression-tab query. */
    onExpressionSearchQueryChange?: (query: string) => void;
    /** Controlled change handler for advanced freetext-tab query. */
    onFreetextSearchQueryChange?: (query: string) => void;
    /** Controlled change handler for custom filter buffer. */
    onCustomSearchQueryChange?: (query: string) => void;
    /** When provided with onSearchAttributesChange, controls the Search In selection (persists after search load). */
    searchAttributes?: string[] | null;
    onSearchAttributesChange?: (attrs: string[]) => void;
    customHeaderContent?: React.ReactNode;
    rowErrors?: Map<string, string>;
    onClearRowError?: (rowKey: string) => void;
    sortState?: SortState | null;
    onSortChange?: (sortState: SortState | null) => void;
    listResponse?: ListResponse<T> | null;
    onRefresh?: () => void | Promise<void>;
    allowedSearchAttributeIds?: string[] | null;
    showEditIcon?: boolean;
    onEditClick?: (item: T) => void;
    /** When true, show selection checkboxes in the table. */
    selectable?: boolean;
    /** Selected items for the checkbox-selection mode. */
    selectedItems?: T[];
    /** Called when selection changes (checkbox mode). */
    onSelectionChange?: (items: T[]) => void;
    showCreateButton?: boolean;
    onCreateClick?: () => void;
    createButtonLabel?: string;
    tableVariant?: 'full' | 'dataOnly';
    bindingConstraintsMap?: Record<string, { AllowedValues?: unknown[]; RegEx?: string }>;
    /** When set, an advanced filter is currently applied; show a badge and allow clearing (reload without filter). */
    appliedAdvancedFilter?: string | null;
    /** Called when user clears the applied advanced filter badge; parent should refetch without filter. */
    onClearAppliedFilter?: () => void;
    /** When set, a simple search is currently applied (so we show one badge; parent controls which is active). */
    appliedSearchQuery?: string | null;
    /** Hidden SCIM filter derived from the applied simple search (for preview count / combined advanced search). */
    appliedDerivedFilter?: string | null;
    /** Backend searchText actually applied for the current simple search. */
    appliedSearchText?: string | null;
    /** When provided, controls the "Expression" checkbox state (omit searchdialect=expression). */
    expressionDialectOmitted?: boolean;
    /** When provided with expressionDialectOmitted, controls the Expression checkbox. */
    onExpressionDialectOmittedChange?: (next: boolean) => void;
    /** When provided, controls how the applied simple-search badge is labeled (last applied state). */
    appliedExpressionDialectOmitted?: boolean;
    /** When provided, controls whether simple+advanced searches can combine. */
    combinedSearchesEnabled?: boolean;
    /** When provided with combinedSearchesEnabled, controls the Combined checkbox. */
    onCombinedSearchesEnabledChange?: (next: boolean) => void;
    /** Called when user opens the Advanced tab; parent can fetch last adv filter and set initialAdvFilter. */
    onAdvancedTabOpen?: () => void;
    /** Fetched last advanced filter for this entity (set by parent after onAdvancedTabOpen); populates Phil manual filter. */
    initialAdvFilter?: string | null;
    /** Optional initial mode for simple search input. */
    initialSimpleSearchMode?: 'simple' | 'simpleCustom';
    /** Called when user toggles simple search input mode. */
    onSimpleSearchModeChange?: (mode: 'simple' | 'simpleCustom') => void;
    /** When provided, controls how the applied simple-search badge is labeled. */
    appliedSimpleSearchType?: 'simple' | 'simpleCustom';
    /** When false, GenericSearch will not write search/filter/page to the URL hash. */
    syncUrlToHash?: boolean;
    /** When true, hide the "Search In" multi-select, Shift+Enter hint, and fullscreen/binding toggles. */
    hideSearchExtras?: boolean;
    /** When true, hide the top pagination row (only show bottom). */
    hideTopPagination?: boolean;
    /** When true, hide the Raw/$ tabs in GenericTable. */
    hideDebugTabs?: boolean;
    /** When provided, replaces GenericTable with a custom renderer (e.g. card view). */
    customDataRenderer?: (props: {
        data: T[];
        isLoading: boolean;
        currentPage: number;
        totalResults: number;
        itemsPerPage: number;
        onPageChange: (page: number) => void;
        onRowClick?: (item: T) => void;
    }) => React.ReactNode;
}

export function GenericSearch<T>({
    entityType,
    data,
    isLoading,
    error,
    currentPage,
    totalResults,
    itemsPerPage,
    onPageChange,
    columns,
    getRowKey,
    onRowClick,
    emptyMessage,
    selectedEntityId,
    deepLinkEntityType,
    onEntitySelect,
    entityService,
    onSearch,
    onAdvancedSearch,
    initialSearchQuery = '',
    onSearchQueryChange,
    simpleSearchQuery: simpleSearchQueryFromParent,
    expressionSearchQuery: expressionSearchQueryFromParent,
    freetextSearchQuery: freetextSearchQueryFromParent,
    customSearchQuery: customSearchQueryFromParent,
    onSimpleSearchQueryChange,
    onExpressionSearchQueryChange,
    onFreetextSearchQueryChange,
    onCustomSearchQueryChange,
    searchAttributes: searchAttributesFromParent,
    onSearchAttributesChange,
    customHeaderContent,
    rowErrors,
    onClearRowError,
    sortState,
    onSortChange,
    listResponse,
    onRefresh,
    allowedSearchAttributeIds,
    showEditIcon,
    onEditClick,
    selectable,
    selectedItems,
    onSelectionChange,
    showCreateButton,
    onCreateClick,
    createButtonLabel,
    tableVariant: _tableVariant = 'dataOnly',
    bindingConstraintsMap,
    appliedAdvancedFilter,
    onClearAppliedFilter,
    appliedSearchQuery,
    appliedDerivedFilter,
    appliedSearchText,
    expressionDialectOmitted: _expressionDialectOmittedFromParent,
    onExpressionDialectOmittedChange: _onExpressionDialectOmittedChange,
    appliedExpressionDialectOmitted,
    combinedSearchesEnabled: combinedSearchesEnabledFromParent,
    onCombinedSearchesEnabledChange,
    onAdvancedTabOpen,
    initialAdvFilter,
    initialSimpleSearchMode = 'simple',
    onSimpleSearchModeChange,
    appliedSimpleSearchType,
    syncUrlToHash = true,
    hideSearchExtras = false,
    hideTopPagination = false,
    hideDebugTabs = false,
    customDataRenderer,
}: GenericSearchProps<T>) {

    const REFRESH_COOLDOWN_MS = 3000;
    const [refreshCooldown, setRefreshCooldown] = useState(false);
    const [showBindingWarnings, setShowBindingWarnings] = useState(true);
    const prevLoadingRef = useRef(isLoading);
    const cooldownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const wasLoading = prevLoadingRef.current;
        prevLoadingRef.current = isLoading;
        if (wasLoading && !isLoading) startRefreshCooldown();
    }, [isLoading]);

    useEffect(() => {
        if (error) startRefreshCooldown();
    }, [error]);

    function startRefreshCooldown() {
        if (cooldownTimeoutRef.current) clearTimeout(cooldownTimeoutRef.current);
        setRefreshCooldown(true);
        cooldownTimeoutRef.current = setTimeout(() => {
            cooldownTimeoutRef.current = null;
            setRefreshCooldown(false);
        }, REFRESH_COOLDOWN_MS);
    }

    useEffect(() => () => {
        if (cooldownTimeoutRef.current) clearTimeout(cooldownTimeoutRef.current);
    }, []);

    const updateUrlHashQueryParams = useCallback((next: { search?: string | null; sd?: 'expression' | 'freetext' | null; filter?: string | null; page?: number | null }, mode: 'replace' | 'push' = 'replace') => {
        if (!syncUrlToHash) return;
        writeTableDeepLinkStateToHash(next, { mode });
    }, [syncUrlToHash]);

    const handleRefresh = useCallback(async () => {
        if (onRefresh && !isLoading && !refreshCooldown) await onRefresh();
    }, [onRefresh, isLoading, refreshCooldown]);

    const [lastProcessedEntityId, setLastProcessedEntityId] = useState<string | null>(null);
    const fetchingEntityIdRef = useRef<string | null>(null);
    const [tableActiveTab, setTableActiveTab] = useState(0);
    const [searchMode, setSearchMode] = useState<'table' | 'advanced'>('table');
    const [internalSimpleSearchQuery, setInternalSimpleSearchQuery] = useState(initialSearchQuery);
    const [internalExpressionSearchQuery, setInternalExpressionSearchQuery] = useState('');
    const [internalFreetextSearchQuery, setInternalFreetextSearchQuery] = useState('');
    const [internalCustomSearchQuery, setInternalCustomSearchQuery] = useState('');
    const [currentAdvancedFilter, setCurrentAdvancedFilter] = useState<string>('');
    const [customFilterBadgeText, setCustomFilterBadgeText] = useState<string | null>(null);
    const [advancedTab, setAdvancedTab] = useState<'expression' | 'freetext' | 'manual' | 'wizard'>('expression');
    const [internalSimpleSearchMode, setInternalSimpleSearchMode] = useState<'simple' | 'simpleCustom'>(initialSimpleSearchMode);

    useEffect(() => {
        setCurrentAdvancedFilter('');
        setCustomFilterBadgeText(null);
        setAdvancedTab('expression');
        setSearchMode('table');
        setInternalSimpleSearchMode(initialSimpleSearchMode);
        setInternalCustomSearchQuery('');
    }, [entityType, initialSimpleSearchMode]);

    const hasPerModeControlledQueries =
        typeof onSimpleSearchQueryChange === 'function' &&
        simpleSearchQueryFromParent !== undefined;

    const [internalSearchAttributes, setInternalSearchAttributes] = useState<string[]>(['DisplayName']);
    // Treat Search In as controlled whenever a change handler is provided (value may be undefined while loading).
    // This prevents the selector from becoming "uncontrolled" and losing updates (e.g. selecting Id not reaching parent).
    const isSearchAttributesControlled = searchAttributesFromParent !== null && typeof onSearchAttributesChange === 'function';
    const searchAttributes = isSearchAttributesControlled ? (searchAttributesFromParent ?? ['DisplayName']) : internalSearchAttributes;

    const isCombinedControlled = typeof combinedSearchesEnabledFromParent === 'boolean' && typeof onCombinedSearchesEnabledChange === 'function';
    const [internalCombinedSearchesEnabled, setInternalCombinedSearchesEnabled] = useState<boolean>(false);
    const combinedSearchesEnabled = isCombinedControlled ? combinedSearchesEnabledFromParent : internalCombinedSearchesEnabled;

    const schemas = useSchemas();
    const allowedSearchAttributesNormalized = React.useMemo(() => {
        if (!Array.isArray(allowedSearchAttributeIds)) return null;
        const seen = new Set<string>();
        const result: string[] = [];
        for (const attr of allowedSearchAttributeIds) {
            const value = String(attr || '').trim();
            const key = value.toLowerCase();
            if (!key || seen.has(key)) continue;
            seen.add(key);
            result.push(value);
        }
        return result;
    }, [allowedSearchAttributeIds]);

    const allowedSet = React.useMemo(() => {
        if (!Array.isArray(allowedSearchAttributeIds)) return null;
        return new Set(allowedSearchAttributeIds.map((a) => (a || '').toLowerCase()));
    }, [allowedSearchAttributeIds]);

    const searchInOptions = React.useMemo(() => {
        let list: string[];
        if (!entityType || !schemas || schemas.length === 0) {
            if (allowedSearchAttributesNormalized && allowedSearchAttributesNormalized.length > 0) {
                // If tier2 already provides allowed search attributes, use them directly
                // so Search In does not collapse to pinned defaults while schema is missing/late.
                list = [...allowedSearchAttributesNormalized];
            } else {
                const result = getSearchInAttributesForSchema(entityType, undefined);
                list = filterSearchInByAllowed(result.all, allowedSet);
            }
        } else {
            const searchType = entityType.toLowerCase();
            const schema = schemas.find((s: any) => {
                const schemaId = (s?.id || '').toLowerCase();
                const schemaName = (s?.name || '').toLowerCase();
                return schemaName === searchType || schemaId === searchType ||
                    schemaId === `urn:dcps:scim:v1:${searchType}` ||
                    schemaId === `urn:ietf:params:scim:schemas:core:2.0:${searchType}` ||
                    schemaId.includes(searchType);
            });
            const available = (schema?.attributes || []).map((a: any) => a?.name).filter(Boolean);
            const result = getSearchInAttributesForSchema(entityType, available);
            list = filterSearchInByAllowed(result.all, allowedSet);
        }
        const prioritized = prioritizePreferredSearchAttributes(list);
        if (prioritized.length > 0) return prioritized;

        // If tier2 provided allowed attributes, do not fall back to static defaults.
        if (allowedSet !== null) return [];

        return prioritizePreferredSearchAttributes(['DisplayName', 'Id']);
    }, [entityType, schemas, allowedSet, allowedSearchAttributesNormalized]);

    useEffect(() => {
        if (isSearchAttributesControlled) return;
        const allowed = new Set(searchInOptions.map((a) => (a || '').toLowerCase()));
        const kept = internalSearchAttributes.filter((a) => allowed.has((a || '').toLowerCase()));
        if (kept.length === internalSearchAttributes.length) return;
        if (kept.length > 0) {
            setInternalSearchAttributes(kept);
        } else {
            const fallback = searchInOptions.length > 0
                ? (searchInOptions.includes('DisplayName') ? ['DisplayName'] : searchInOptions.slice(0, 1).filter(Boolean))
                : [];
            setInternalSearchAttributes(fallback);
        }
    }, [searchInOptions, isSearchAttributesControlled, internalSearchAttributes]);

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const hasProcessedEmptySearchRef = useRef<boolean>(false);
    const lastProcessedQueryRef = useRef<string>('');
    const lastSearchTimestampRef = useRef<number>(0);
    const searchInProgressRef = useRef<boolean>(false);
    const currentSearchQueryRef = useRef<string>(initialSearchQuery);
    const simpleSearchInputRef = useRef<HTMLInputElement | null>(null);
    const customSearchEditorRef = useRef<any | null>(null);
    const restoreSearchInputFocusRef = useRef<boolean>(false);
    const [hasActiveSearch, setHasActiveSearch] = useState<boolean>(false);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

    const handleFullscreenToggle = () => setIsFullscreen(!isFullscreen);

    const lastUserInputAtRef = useRef<number>(0);

    useEffect(() => {
        const msSinceLastInput = Date.now() - lastUserInputAtRef.current;
        const USER_TYPING_GRACE_MS = 600;
        if (msSinceLastInput < USER_TYPING_GRACE_MS) return;
        // When parent provides per-mode draft buffers, do not sync initialSearchQuery into internal state.
        if (hasPerModeControlledQueries) return;
        if (initialSearchQuery !== internalSimpleSearchQuery) setInternalSimpleSearchQuery(initialSearchQuery);
    }, [initialSearchQuery, internalSimpleSearchQuery, hasPerModeControlledQueries]);

    const simpleSearchQuery = hasPerModeControlledQueries
        ? (simpleSearchQueryFromParent as string)
        : internalSimpleSearchQuery;
    const expressionSearchQuery = expressionSearchQueryFromParent ?? internalExpressionSearchQuery;
    const freetextSearchQuery = freetextSearchQueryFromParent ?? internalFreetextSearchQuery;
    const customSearchQuery = customSearchQueryFromParent ?? internalCustomSearchQuery;
    const simpleSearchMode = typeof onSimpleSearchModeChange === 'function'
        ? initialSimpleSearchMode
        : internalSimpleSearchMode;

    useEffect(() => { currentSearchQueryRef.current = simpleSearchQuery; }, [simpleSearchQuery]);
    const expressionSearchQueryRef = useRef<string>(expressionSearchQuery);
    const freetextSearchQueryRef = useRef<string>(freetextSearchQuery);
    const customSearchQueryRef = useRef<string>(customSearchQuery);
    useEffect(() => { expressionSearchQueryRef.current = expressionSearchQuery; }, [expressionSearchQuery]);
    useEffect(() => { freetextSearchQueryRef.current = freetextSearchQuery; }, [freetextSearchQuery]);
    useEffect(() => { customSearchQueryRef.current = customSearchQuery; }, [customSearchQuery]);
    useEffect(() => {
        if (!restoreSearchInputFocusRef.current) return;
        restoreSearchInputFocusRef.current = false;
        requestAnimationFrame(() => {
            const el = simpleSearchInputRef.current;
            if (!el) return;
            el.focus();
            const end = el.value?.length ?? 0;
            if ('setSelectionRange' in el) {
                el.setSelectionRange(end, end);
            }
        });
    }, [simpleSearchMode]);

    useEffect(() => {
        if (simpleSearchMode !== 'simpleCustom') return;
        requestAnimationFrame(() => {
            const ed = customSearchEditorRef.current;
            if (!ed) return;
            ed.focus();
            ed.navigateFileEnd();
        });
    }, [simpleSearchMode]);

    useEffect(() => {
        const handleDeepLinking = async () => {
            if (selectedEntityId && deepLinkEntityType && deepLinkEntityType.toLowerCase() === entityType.toLowerCase() && onEntitySelect) {
                if (selectedEntityId === lastProcessedEntityId || selectedEntityId === fetchingEntityIdRef.current) return;
                const targetEntity = data.find(item => getRowKey(item) === selectedEntityId);
                if (targetEntity) {
                    setLastProcessedEntityId(selectedEntityId);
                    onEntitySelect(targetEntity);
                } else if (entityService?.getEntityById) {
                    try {
                        fetchingEntityIdRef.current = selectedEntityId;
                        const fetchedEntity = await entityService.getEntityById(selectedEntityId);
                        setLastProcessedEntityId(selectedEntityId);
                        fetchingEntityIdRef.current = null;
                        onEntitySelect(fetchedEntity);
                    } catch (err) {
                        console.error(`GenericSearch: Failed to fetch ${entityType} with ID ${selectedEntityId}:`, err);
                        fetchingEntityIdRef.current = null;
                    }
                }
            } else if (!selectedEntityId) {
                setLastProcessedEntityId(null);
                fetchingEntityIdRef.current = null;
            }
        };
        handleDeepLinking();
    }, [selectedEntityId, deepLinkEntityType, data, entityType, onEntitySelect, getRowKey, lastProcessedEntityId, entityService]);

    useEffect(() => {
        if (hasPerModeControlledQueries) return;
        if (onSearchQueryChange) return;
        setInternalSimpleSearchQuery(initialSearchQuery);
    }, [initialSearchQuery, onSearchQueryChange, hasPerModeControlledQueries]);

    const searchWasClearedRef = useRef<boolean>(false);
    const hasProcessedInitialEmptySearchRef = useRef<boolean>(false);

    useEffect(() => {
        if (initialSearchQuery === '') {
            if (!hasProcessedInitialEmptySearchRef.current) {
                lastProcessedQueryRef.current = '';
                setActiveSearchQuery('');
                searchWasClearedRef.current = true;
            }
        } else if (initialSearchQuery !== simpleSearchQuery) {
            searchWasClearedRef.current = false;
            hasProcessedInitialEmptySearchRef.current = false;
        }
    }, [initialSearchQuery, simpleSearchQuery]);

    const [activeSearchQuery, setActiveSearchQuery] = useState<string>('');

    const onSearchRef = useRef(onSearch);
    useEffect(() => { onSearchRef.current = onSearch; }, [onSearch]);
    const searchAttributesRef = useRef(searchAttributes);
    useEffect(() => { searchAttributesRef.current = searchAttributes; }, [searchAttributes]);
    const setSearchAttributesRefSynchronously = useCallback((attrs: string[]) => {
        searchAttributesRef.current = attrs;
    }, []);

    const lastActiveSearchQueryRef = useRef<string>('');

    useEffect(() => {
        if (activeSearchQuery === lastActiveSearchQueryRef.current) return;
        lastActiveSearchQueryRef.current = activeSearchQuery;
        const now = Date.now();
        const timeSinceLastSearch = now - lastSearchTimestampRef.current;
        const MIN_SEARCH_INTERVAL = 600;

        if (activeSearchQuery === '' && hasProcessedInitialEmptySearchRef.current &&
            activeSearchQuery === lastProcessedQueryRef.current) {
            if (!searchWasClearedRef.current || timeSinceLastSearch < MIN_SEARCH_INTERVAL) return;
        }

        const shouldSearch = activeSearchQuery !== lastProcessedQueryRef.current ||
            (searchWasClearedRef.current && activeSearchQuery !== '' && lastProcessedQueryRef.current === '__CLEARED__') ||
            (activeSearchQuery === lastProcessedQueryRef.current && timeSinceLastSearch >= MIN_SEARCH_INTERVAL);

        if (shouldSearch) {
            lastProcessedQueryRef.current = activeSearchQuery;
            lastSearchTimestampRef.current = now;
            searchWasClearedRef.current = false;
            if (activeSearchQuery === '') hasProcessedInitialEmptySearchRef.current = true;
            setHasActiveSearch(activeSearchQuery.trim() !== '');
            if (onSearchRef.current) {
                debugLog('GenericSearch: Calling onSearch with:', activeSearchQuery, 'searchAttributes:', searchAttributesRef.current);
                searchInProgressRef.current = true;
                onSearchRef.current(
                    activeSearchQuery.trim(),
                    searchAttributesRef.current,
                    null,
                    'simple'
                );
                setTimeout(() => { searchInProgressRef.current = false; }, 100);
            }
        }
    }, [activeSearchQuery]);

    const externalQueryDebounceRef = React.useRef<number | null>(null);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            if (externalQueryDebounceRef.current) window.clearTimeout(externalQueryDebounceRef.current);
        };
    }, []);

    const handlePageChange = (page: number) => {
        updateUrlHashQueryParams({
            search: appliedSearchQuery,
            filter: appliedAdvancedFilter,
            page,
        }, 'push');
        onPageChange(page);
    };

    const handleRowClick = (item: T) => {
        if (onRowClick) onRowClick(item);
        if (onEntitySelect) onEntitySelect(item);
    };

    const handleSimpleSearchChange = (query: string) => {
        currentSearchQueryRef.current = query;
        lastUserInputAtRef.current = Date.now();
        if (hasPerModeControlledQueries) {
            onSimpleSearchQueryChange?.(query);
            return;
        }
        setInternalSimpleSearchQuery(query);
        if (onSearchQueryChange) {
            if (externalQueryDebounceRef.current) window.clearTimeout(externalQueryDebounceRef.current);
            externalQueryDebounceRef.current = window.setTimeout(() => { onSearchQueryChange(query); }, 150);
        }
    };

    const handleExpressionSearchChange = useCallback((query: string) => {
        expressionSearchQueryRef.current = query;
        lastUserInputAtRef.current = Date.now();
        if (onExpressionSearchQueryChange) {
            onExpressionSearchQueryChange(query);
            return;
        }
        setInternalExpressionSearchQuery(query);
    }, [onExpressionSearchQueryChange]);

    const handleFreetextSearchChange = useCallback((query: string) => {
        freetextSearchQueryRef.current = query;
        lastUserInputAtRef.current = Date.now();
        if (onFreetextSearchQueryChange) {
            onFreetextSearchQueryChange(query);
            return;
        }
        setInternalFreetextSearchQuery(query);
    }, [onFreetextSearchQueryChange]);

    const handleCustomSearchChange = useCallback((query: string) => {
        customSearchQueryRef.current = query;
        lastUserInputAtRef.current = Date.now();
        if (onCustomSearchQueryChange) {
            onCustomSearchQueryChange(query);
            return;
        }
        setInternalCustomSearchQuery(query);
    }, [onCustomSearchQueryChange]);

    const setSimpleSearchModeState = useCallback((mode: 'simple' | 'simpleCustom') => {
        if (onSimpleSearchModeChange) {
            onSimpleSearchModeChange(mode);
            return;
        }
        setInternalSimpleSearchMode(mode);
    }, [onSimpleSearchModeChange]);

    const switchToCustomSimpleSearch = useCallback((seedQuery?: string) => {
        const seed = seedQuery ?? currentSearchQueryRef.current ?? '';
        if (seed.trim()) {
            handleCustomSearchChange(seed);
        }
        setSimpleSearchModeState('simpleCustom');
    }, [handleCustomSearchChange, setSimpleSearchModeState]);

    const handleCustomSimpleSearchSubmit = useCallback((overrideQuery?: string) => {
        const filterString = (overrideQuery ?? customSearchQueryRef.current ?? '').trim();
        if (!filterString) return;
        setCustomFilterBadgeText(filterString);
        if (onAdvancedSearch) {
            onAdvancedSearch({
                filter: filterString,
                isCustomSimpleFilter: true,
                searchAttributes: searchAttributesRef.current ?? undefined
            });
        }
    }, [onAdvancedSearch]);

    const handleCustomSimpleSearchSubmitRef = useRef(handleCustomSimpleSearchSubmit);
    handleCustomSimpleSearchSubmitRef.current = handleCustomSimpleSearchSubmit;
    const setSimpleSearchModeStateRef = useRef(setSimpleSearchModeState);
    setSimpleSearchModeStateRef.current = setSimpleSearchModeState;

    const handleManualScimAceLoad = useCallback((editor: any) => {
        editor.commands.addCommand({
            name: 'manualScimSubmit',
            bindKey: { win: 'Enter', mac: 'Enter' },
            exec: (ed: any) => {
                handleCustomSimpleSearchSubmitRef.current(ed.getValue());
                return true;
            },
        });
        editor.commands.addCommand({
            name: 'manualScimNewline',
            bindKey: { win: 'Shift-Enter', mac: 'Shift-Enter' },
            exec: (ed: any) => {
                ed.insert('\n');
                return true;
            },
        });
        editor.commands.addCommand({
            name: 'manualScimEscape',
            bindKey: { win: 'Escape', mac: 'Escape' },
            exec: () => {
                setSimpleSearchModeStateRef.current('simple');
                restoreSearchInputFocusRef.current = true;
                return true;
            },
        });
    }, []);

    const handleSimpleSearchSubmit = () => {
        if (simpleSearchMode === 'simpleCustom') {
            handleCustomSimpleSearchSubmit();
            return;
        }
        let queryToSearch = (currentSearchQueryRef.current || '').trim();
        // If box is empty but we have an applied simple search (e.g. both badges showing), re-apply it so search is not lost
        if (!queryToSearch && appliedSearchQuery?.trim()) {
            queryToSearch = appliedSearchQuery.trim();
            setInternalSimpleSearchQuery(queryToSearch);
            currentSearchQueryRef.current = queryToSearch;
        }
        if (queryToSearch && detectAutoSearchKind(queryToSearch) === 'manual') {
            switchToCustomSimpleSearch(queryToSearch);
            handleCustomSimpleSearchSubmit(queryToSearch);
            return;
        }
        if (!queryToSearch) {
            lastProcessedQueryRef.current = '__CLEARED__';
            searchWasClearedRef.current = true;
            setHasActiveSearch(false);
            if (onSearchRef.current) onSearchRef.current('', undefined, null, 'simple');
            updateUrlHashQueryParams({ search: '', filter: appliedAdvancedFilter, page: 1 }, 'push');
        } else {
            const now = Date.now();
            const timeSinceLastSearch = now - lastSearchTimestampRef.current;
            const MIN_SEARCH_INTERVAL = 600;
            if (queryToSearch === lastProcessedQueryRef.current && timeSinceLastSearch >= MIN_SEARCH_INTERVAL) {
                lastProcessedQueryRef.current = queryToSearch;
                lastSearchTimestampRef.current = now;
                searchWasClearedRef.current = false;
                setHasActiveSearch(true);
                setActiveSearchQuery(queryToSearch);
                if (onSearchRef.current) onSearchRef.current(queryToSearch, searchAttributesRef.current, null, 'simple');
                updateUrlHashQueryParams({ search: queryToSearch, filter: appliedAdvancedFilter, page: 1 }, 'push');
            } else if (queryToSearch !== lastProcessedQueryRef.current) {
                setActiveSearchQuery(queryToSearch);
            } else {
                setActiveSearchQuery(queryToSearch);
                // Same query as before but user asked to search again (e.g. re-apply); trigger load
                lastProcessedQueryRef.current = queryToSearch;
                lastSearchTimestampRef.current = now;
                searchWasClearedRef.current = false;
                setHasActiveSearch(true);
                if (onSearchRef.current) onSearchRef.current(queryToSearch, searchAttributesRef.current, null, 'simple');
                updateUrlHashQueryParams({ search: queryToSearch, filter: appliedAdvancedFilter, page: 1 }, 'push');
            }
        }
    };

    const handleSimpleSearchKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (event.shiftKey && simpleSearchMode !== 'simpleCustom') {
                switchToCustomSimpleSearch();
                return;
            }
            handleSimpleSearchSubmit();
        }
    };

    const handleClearSearch = () => {
        const hadAppliedSimple = (appliedSearchQuery != null && appliedSearchQuery.trim() !== '') || (appliedSearchQuery === undefined && hasActiveSearch);
        if (onSearchQueryChange) onSearchQueryChange('');
        else setInternalSimpleSearchQuery('');
        currentSearchQueryRef.current = '';
        lastProcessedQueryRef.current = '__CLEARED__';
        searchWasClearedRef.current = true;
        setActiveSearchQuery('');
        setHasActiveSearch(false);
        if (hadAppliedSimple && onSearchRef.current) onSearchRef.current('', undefined, null, 'simple');
    };

    /** Clear applied simple search from the table (reload without search) but keep the search box text so user can re-apply. */
    const handleClearAppliedSimpleSearch = useCallback(() => {
        lastProcessedQueryRef.current = '';
        searchWasClearedRef.current = true;
        setHasActiveSearch(false);
        setActiveSearchQuery('');
        if (onSearchRef.current) onSearchRef.current('', searchAttributesRef.current, null, 'simple');
    }, []);

    const hasAppliedSimple = (appliedSearchQuery != null && appliedSearchQuery.trim() !== '') || (appliedSearchQuery === undefined && hasActiveSearch);
    const hasAppliedAdvanced = appliedAdvancedFilter != null && appliedAdvancedFilter.trim() !== '';
    const hasAppliedFilter = hasAppliedSimple || hasAppliedAdvanced;
    const simpleSearchLabel = appliedSearchQuery !== undefined && appliedSearchQuery !== null ? appliedSearchQuery : activeSearchQuery;
    // When parent does not provide appliedSearchQuery (or provides undefined), fall back to the internal applied query
    // so advanced apply combines with the visible simple badge.
    const appliedSimpleForAdvanced = ((appliedSearchQuery ?? activeSearchQuery) || '').trim() || undefined;
    const truncateForBadge = (s: string, maxLen: number = 40) =>
        s.length <= maxLen ? s : s.slice(0, maxLen - 3) + '...';
    const transformedSimpleBadgeText = React.useMemo(() => {
        const raw = (simpleSearchLabel || '').trim();
        if (!raw) return '';
        return `search-auto: ${truncateForBadge(raw)}`;
    }, [simpleSearchLabel]);

    const encodeForHashParamValue = React.useCallback((key: string, raw: string): string => {
        const params = new URLSearchParams();
        params.set(key, raw ?? '');
        const encoded = params.toString();
        // encoded is "key=VALUE" (or empty if key is empty).
        return encoded.startsWith(`${key}=`) ? encoded.slice(key.length + 1) : encoded;
    }, []);

    const makeRawAndEncodedTitle = React.useCallback((key: string, raw: string): string => {
        const rawValue = (raw ?? '').toString();
        const encodedValue = encodeForHashParamValue(key, rawValue);
        return `Raw: ${rawValue} | Encoded: ${encodedValue}`;
    }, [encodeForHashParamValue]);
    const transformedSimpleBadgeTitle = React.useMemo(() => {
        const raw = (simpleSearchLabel || '').trim();
        const actual = (appliedSearchText || '').trim();
        const derived = (appliedDerivedFilter || '').trim();
        const base = makeRawAndEncodedTitle('search', raw);
        if (!raw) return base;
        if (!actual || (actual === raw && !derived)) return base;
        const parts = [`Applied searchText: ${actual}`];
        if (derived) parts.push(`Applied filter: ${derived}`);
        return `${base} | ${parts.join(' | ')}`;
    }, [appliedDerivedFilter, appliedSearchText, makeRawAndEncodedTitle, simpleSearchLabel]);

    const handleAdvancedSearch = (filters: any) => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSearchMode('advanced');
        setCustomFilterBadgeText(null);
        if (onAdvancedSearch) onAdvancedSearch(filters);
    };

    const handleExplicitDialectSearch = useCallback((dialect: 'expression' | 'freetext') => {
        const queryToSearch = (dialect === 'expression'
            ? expressionSearchQueryRef.current
            : freetextSearchQueryRef.current
        ).trim();
        if (!queryToSearch) return;

        const nextOmit = dialect === 'freetext';
        const now = Date.now();
        lastProcessedQueryRef.current = queryToSearch;
        lastSearchTimestampRef.current = now;
        searchWasClearedRef.current = false;
        setHasActiveSearch(true);
        setActiveSearchQuery(queryToSearch);
        currentSearchQueryRef.current = queryToSearch;
        if (hasPerModeControlledQueries) onSimpleSearchQueryChange?.(queryToSearch);
        else setInternalSimpleSearchQuery(queryToSearch);

        if (onSearchRef.current) {
            onSearchRef.current(queryToSearch, searchAttributesRef.current, nextOmit, 'simple');
        }

        updateUrlHashQueryParams({
            search: queryToSearch,
            sd: dialect,
            filter: appliedAdvancedFilter,
            page: 1
        }, 'push');
        handleBackToTableAfterFilter();
    }, [appliedAdvancedFilter, hasPerModeControlledQueries, onSimpleSearchQueryChange, updateUrlHashQueryParams]);

    const handleBackToTable = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSearchMode('table');
    };

    const handleBackToTableAfterFilter = () => setSearchMode('table');

    const scoreRange = React.useMemo(() => {
        if (!simpleSearchQuery?.trim() || !data || data.length === 0 || isLoading) return null;
        return extractScoreRange(data);
    }, [data, simpleSearchQuery, isLoading]);

    const searchPlaceholder = React.useMemo(() => {
        if (hideSearchExtras) {
            return `Type to search and press Enter...`;
        }
        const activeAttributes = searchAttributes.length > 0 ? searchAttributes : ['DisplayName'];
        const attributeText = activeAttributes.length === 1 ? activeAttributes[0] : activeAttributes.join(', ');
        return entityType.toLowerCase() === 'person'
            ? `Search by ${attributeText} (Press Enter)...`
            : `Search ${getPluralizedLabel(entityType).toLowerCase()} by ${attributeText} (Press Enter)...`;
    }, [searchAttributes, entityType, hideSearchExtras]);

    const searchInSelectData = React.useMemo(() => {
        const highPriority = searchInOptions.filter(isHighPrioritySearchAttribute);
        const others = searchInOptions.filter((attr) => !isHighPrioritySearchAttribute(attr));

        if (highPriority.length > 0 && others.length > 0) {
            return [
                {
                    group: 'High Priority',
                    items: highPriority.map((attr) => ({ value: attr, label: attr }))
                },
                {
                    group: 'Other Search Attributes',
                    items: others.map((attr) => ({ value: attr, label: attr }))
                }
            ];
        }

        return searchInOptions.map((attr) => ({ value: attr, label: attr }));
    }, [searchInOptions]);

    const scimFilterEditorCompletions = useMemo(
        () => buildScimFilterEditorCompletions(searchInOptions),
        [searchInOptions]
    );

    const createLabel = createButtonLabel ?? (entityType ? `Create ${getSingularLabel(entityType)}` : 'Create');

    return (
        <Box w="100%">
            {!isFullscreen && (
                <Group gap="xs" mb={customHeaderContent || onCreateClick ? 'md' : 0} wrap="wrap">
                    {onCreateClick && (
                        <Tooltip label={!showCreateButton ? "You don't have permission to create this entity type." : undefined} disabled={showCreateButton}>
                            <span style={{ display: 'inline-block' }}>
                                <Button
                                    leftSection={<IconPlus size={16} />}
                                    onClick={onCreateClick}
                                    size="sm"
                                    disabled={!showCreateButton}
                                >
                                    {createLabel}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {customHeaderContent}
                </Group>
            )}

            {error && (
                <Alert color="red" mb="md" icon={<IconAlertTriangle size={16} />}>
                    {error}
                </Alert>
            )}

            {!isFullscreen && (
                <Paper shadow="xs" p="md" mb="sm">
                    {searchMode === 'table' ? (
                        <Box>
                            {simpleSearchMode === 'simpleCustom' ? (
                                <Group align="flex-end" gap="sm" wrap="wrap">
                                    <Box flex={1} miw={240}>
                                        <Stack gap="sm">
                                            <Paper
                                                radius="md"
                                                p={6}
                                                styles={{
                                                    root: {
                                                        border: '1px solid var(--mantine-color-blue-4)',
                                                        backgroundColor: 'var(--mantine-color-default)',
                                                        transition: 'border-color 120ms ease, box-shadow 120ms ease',
                                                        '&:focus-within': {
                                                            borderColor: 'var(--mantine-color-blue-filled)',
                                                            boxShadow: '0 0 0 2px rgba(34, 139, 230, 0.25)',
                                                        },
                                                    },
                                                }}
                                            >
                                                <Box p={4}>
                                                    <ScimAceEditor
                                                        mode="text"
                                                        value={customSearchQuery}
                                                        onChange={handleCustomSearchChange}
                                                        height="220px"
                                                        fontSize={13}
                                                        showLineNumbers={false}
                                                        showGutter={false}
                                                        showPrintMargin={false}
                                                        highlightActiveLine={false}
                                                        name="generic-search-manual-scim"
                                                        placeholder={`Type or paste a SCIM filter for ${entityType}`}
                                                        staticCompletions={scimFilterEditorCompletions}
                                                        editorInstanceRef={customSearchEditorRef}
                                                        onLoad={handleManualScimAceLoad}
                                                        setOptions={{
                                                            wrap: true,
                                                            fontFamily:
                                                                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                                                        }}
                                                    />
                                                </Box>
                                            </Paper>
                                            <Group justify="space-between" gap="xs" wrap="wrap">
                                                <Text size="xs" c="dimmed">
                                                    Manual SCIM filter mode. Press `Enter` to search, `Shift+Enter` for a newline, `Esc` to return. Use `Ctrl+Space` for attribute and operator suggestions.
                                                </Text>
                                                <Group gap="xs" wrap="wrap">
                                                    {!!customSearchQuery?.trim() && (
                                                        <ActionIcon
                                                            size="sm"
                                                            variant="subtle"
                                                            onClick={() => handleCustomSearchChange('')}
                                                            aria-label="clear custom search"
                                                        >
                                                            <IconX size={14} />
                                                        </ActionIcon>
                                                    )}
                                                    <Button
                                                        variant="subtle"
                                                        size="compact-xs"
                                                        onClick={() => {
                                                            setSimpleSearchModeState('simple');
                                                            restoreSearchInputFocusRef.current = true;
                                                        }}
                                                    >
                                                        Simple Search
                                                    </Button>
                                                </Group>
                                            </Group>
                                        </Stack>
                                    </Box>
                                    <Button
                                        onClick={() => handleCustomSimpleSearchSubmit()}
                                        leftSection={<IconSearch size={16} />}
                                        size="sm"
                                        disabled={!customSearchQuery?.trim()}
                                    >
                                        Search
                                    </Button>
                                </Group>
                            ) : (
                                <>
                                    {hideSearchExtras ? (
                                        <Stack gap="xs">
                                            <Group gap="sm" grow preventGrowOverflow={false} wrap="wrap">
                                                <Select
                                                    size="sm"
                                                    data={searchInSelectData}
                                                    value={searchAttributes.length > 0 ? searchAttributes[0] : 'DisplayName'}
                                                    onChange={(val) => {
                                                        if (!val) return;
                                                        setSearchAttributesRefSynchronously([val]);
                                                        if (onSearchAttributesChange) {
                                                            onSearchAttributesChange([val]);
                                                        } else {
                                                            setInternalSearchAttributes([val]);
                                                        }
                                                    }}
                                                    aria-label="Search In"
                                                    style={{ flex: '0 0 auto', minWidth: 160, maxWidth: 200 }}
                                                    allowDeselect={false}
                                                />
                                                <TextInput
                                                    ref={simpleSearchInputRef}
                                                    placeholder={`Search by ${(searchAttributes.length > 0 ? searchAttributes[0] : 'name').toLowerCase()}...`}
                                                    value={simpleSearchQuery}
                                                    onChange={(e) => handleSimpleSearchChange(e.currentTarget.value)}
                                                    onKeyDown={handleSimpleSearchKeyPress}
                                                    size="md"
                                                    style={{ flex: 1 }}
                                                    leftSection={
                                                        <ActionIcon
                                                            size="sm"
                                                            variant="subtle"
                                                            onClick={handleSimpleSearchSubmit}
                                                            disabled={!simpleSearchQuery?.trim() && !hasActiveSearch}
                                                            aria-label="search"
                                                        >
                                                            <IconSearch size={16} />
                                                        </ActionIcon>
                                                    }
                                                    rightSection={
                                                        (simpleSearchQuery || hasActiveSearch) ? (
                                                            <ActionIcon
                                                                size="sm"
                                                                variant="subtle"
                                                                onClick={handleClearSearch}
                                                                aria-label="clear search"
                                                            >
                                                                <IconX size={14} />
                                                            </ActionIcon>
                                                        ) : undefined
                                                    }
                                                />
                                                <Button
                                                    variant="outline"
                                                    leftSection={<IconAdjustments size={16} />}
                                                    onClick={() => {
                                                        onAdvancedTabOpen?.();
                                                        setAdvancedTab(appliedAdvancedFilter?.trim() ? 'manual' : 'expression');
                                                        setSearchMode('advanced');
                                                    }}
                                                    size="sm"
                                                    style={{ flex: '0 0 auto' }}
                                                >
                                                    Advanced
                                                </Button>
                                            </Group>
                                            {!customDataRenderer && totalResults > 0 && !isLoading && (
                                                <Text size="xs" c="dimmed">
                                                    {totalResults.toLocaleString()} {totalResults === 1 ? 'result' : 'results'}
                                                    {(appliedSearchQuery || appliedAdvancedFilter) ? ' matched' : ''}
                                                </Text>
                                            )}
                                        </Stack>
                                    ) : (
                                    <Grid gutter="sm">
                                        <Grid.Col span={{ base: 12, md: 6 }}>
                                            <Stack gap={4}>
                                                <Text size="sm" fw={500} style={{ visibility: 'hidden' }} aria-hidden>
                                                    Search In
                                                </Text>
                                                <TextInput
                                                    ref={simpleSearchInputRef}
                                                    placeholder={searchPlaceholder}
                                                    value={simpleSearchQuery}
                                                    onChange={(e) => handleSimpleSearchChange(e.currentTarget.value)}
                                                    onKeyDown={handleSimpleSearchKeyPress}
                                                    size="sm"
                                                    leftSection={
                                                        <ActionIcon
                                                            size="sm"
                                                            variant="subtle"
                                                            onClick={handleSimpleSearchSubmit}
                                                            disabled={!simpleSearchQuery?.trim() && !hasActiveSearch}
                                                            aria-label="search"
                                                        >
                                                            <IconSearch size={14} />
                                                        </ActionIcon>
                                                    }
                                                    rightSection={
                                                        (simpleSearchQuery || hasActiveSearch) ? (
                                                            <ActionIcon
                                                                size="sm"
                                                                variant="subtle"
                                                                onClick={handleClearSearch}
                                                                aria-label="clear search"
                                                            >
                                                                <IconX size={14} />
                                                            </ActionIcon>
                                                        ) : undefined
                                                    }
                                                />
                                            </Stack>
                                        </Grid.Col>

                                        <Grid.Col span={{ base: 12, md: 4 }}>
                                            <Stack gap={4}>
                                                <Text size="sm" fw={500}>Search In</Text>
                                                <MultiSelect
                                                    size="sm"
                                                    data={searchInSelectData}
                                                    value={searchAttributes}
                                                    onChange={(next) => {
                                                        setSearchAttributesRefSynchronously(next);
                                                        if (onSearchAttributesChange) {
                                                            onSearchAttributesChange(next);
                                                        } else {
                                                            setInternalSearchAttributes(next);
                                                        }
                                                    }}
                                                    aria-label="Search In"
                                                    style={{ minWidth: 150 }}
                                                    clearable={false}
                                                />
                                            </Stack>
                                        </Grid.Col>

                                        <Grid.Col span={{ base: 12, md: 2 }} style={{ display: 'flex', alignItems: 'flex-end' }}>
                                            <Stack gap={4} style={{ width: '100%' }}>
                                                <Text size="sm" fw={500} style={{ visibility: 'hidden' }} aria-hidden>
                                                    Search In
                                                </Text>
                                                <Button
                                                    variant="outline"
                                                    leftSection={<IconAdjustments size={16} />}
                                                    onClick={() => {
                                                        onAdvancedTabOpen?.();
                                                        setAdvancedTab(appliedAdvancedFilter?.trim() ? 'manual' : 'expression');
                                                        setSearchMode('advanced');
                                                    }}
                                                    size="sm"
                                                    style={{ alignSelf: 'stretch' }}
                                                >
                                                    Advanced
                                                </Button>
                                            </Stack>
                                        </Grid.Col>
                                    </Grid>
                                    )}

                                    {!hideSearchExtras && (
                                        <Box mt="xs">
                                            <Text size="xs" c="dimmed" ml={4}>
                                                Press `Shift+Enter` to switch to manual SCIM filter input.
                                            </Text>
                                            {scoreRange && (
                                                <Text size="xs" c="dimmed" mt={4} ml={4}>
                                                    Score range: {scoreRange.max} .. {scoreRange.min}
                                                </Text>
                                            )}
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                    ) : (
                        <Box>
                            <Group mb="md" justify="space-between" align="center" wrap="wrap">
                                <Button variant="outline" onClick={handleBackToTable}>
                                    &larr; Back to Table
                                </Button>
                                <Tooltip label="Different searches are applied together. When unchecked, applying one clears the other so you only have one active badge.">
                                    <Checkbox
                                        label="Combine"
                                        checked={combinedSearchesEnabled}
                                        onChange={(e) => {
                                            const next = e.currentTarget.checked;
                                            if (isCombinedControlled) onCombinedSearchesEnabledChange?.(next);
                                            else setInternalCombinedSearchesEnabled(next);
                                        }}
                                        size="sm"
                                    />
                                </Tooltip>
                            </Group>
                            <Tabs value={advancedTab} onChange={(value) => setAdvancedTab((value as typeof advancedTab) ?? 'expression')}>
                                <Tabs.List>
                                    <Tabs.Tab value="expression">
                                        Expression
                                    </Tabs.Tab>
                                    <Tabs.Tab value="freetext">
                                        Freetext
                                    </Tabs.Tab>
                                    <Tabs.Tab value="manual">
                                        Manual Filter
                                    </Tabs.Tab>
                                    <Tabs.Tab value="wizard">
                                        Filter Wizard
                                    </Tabs.Tab>
                                </Tabs.List>
                            </Tabs>

                            {(advancedTab === 'expression' || advancedTab === 'freetext') && (
                                <Box pt="md">
                                    <Grid gutter="sm" align="flex-end">
                                        <Grid.Col span={{ base: 12, md: 5 }}>
                                            <TextInput
                                                placeholder={searchPlaceholder}
                                                value={advancedTab === 'expression' ? expressionSearchQuery : freetextSearchQuery}
                                                onChange={(e) => {
                                                    if (advancedTab === 'expression') handleExpressionSearchChange(e.currentTarget.value);
                                                    else handleFreetextSearchChange(e.currentTarget.value);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleExplicitDialectSearch(advancedTab);
                                                    }
                                                }}
                                                size="sm"
                                                label="Query"
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={{ base: 12, md: 5 }}>
                                            <MultiSelect
                                                size="sm"
                                                data={searchInSelectData}
                                                value={searchAttributes}
                                                onChange={(next) => {
                                                    setSearchAttributesRefSynchronously(next);
                                                    if (onSearchAttributesChange) {
                                                        onSearchAttributesChange(next);
                                                    } else {
                                                        setInternalSearchAttributes(next);
                                                    }
                                                }}
                                                label="Search In"
                                                style={{ minWidth: 220 }}
                                                clearable={false}
                                            />
                                        </Grid.Col>
                                        <Grid.Col span={{ base: 12, md: 2 }} style={{ display: 'flex', alignItems: 'flex-end' }}>
                                            <Button
                                                onClick={() => handleExplicitDialectSearch(advancedTab)}
                                                leftSection={<IconSearch size={16} />}
                                                size="sm"
                                                disabled={!(advancedTab === 'expression' ? expressionSearchQuery : freetextSearchQuery)?.trim()}
                                                style={{ alignSelf: 'stretch' }}
                                            >
                                                Search
                                            </Button>
                                        </Grid.Col>
                                    </Grid>
                                    <Text size="xs" c="dimmed" mt="xs">
                                        {advancedTab === 'expression'
                                            ? (
                                                <>
                                                    Uses `searchdialect=expression` with the current query.
                                                    {' '}
                                                    <Anchor
                                                        href="#other/Content/view/K4WTBW13CJON.BYR4"
                                                        aria-label="Open Expression help"
                                                    >
                                                        Expression help
                                                    </Anchor>
                                                </>
                                            )
                                            : 'Uses `searchdialect=freetext` with the current query.'}
                                    </Text>
                                </Box>
                            )}

                            {(advancedTab === 'manual' || advancedTab === 'wizard') && (
                                <Box pt="md">
                                    <Phil
                                        entityType={entityType}
                                        initialFilter={initialAdvFilter != null ? initialAdvFilter : currentAdvancedFilter}
                                        currentSimpleSearchText={appliedSimpleForAdvanced}
                                        currentSimpleDerivedFilter={appliedDerivedFilter}
                                        currentSearchAttributes={searchAttributesRef.current ?? undefined}
                                        forcedMode={advancedTab}
                                        hideModeTabs={true}
                                        onApplyFilter={(filterString: string) => {
                                            setCurrentAdvancedFilter(filterString);
                                            setCustomFilterBadgeText(advancedTab === 'manual' ? filterString : null);
                                            handleBackToTableAfterFilter();
                                            if (onAdvancedSearch) onAdvancedSearch({
                                                filter: filterString,
                                                searchtext: appliedSimpleForAdvanced,
                                                searchAttributes: searchAttributesRef.current ?? undefined
                                            });
                                        }}
                                        showManualInput={true}
                                        showBuilder={true}
                                        showGeneratedFilter={true}
                                        compact={false}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                </Paper>
            )}

            {isLoading && !data.length ? (
                <LoadingState variant="centered" size="lg" minHeight={400} />
            ) : searchMode === 'table' ? (
                <>
                    {hasAppliedFilter && (tableActiveTab === 0 || tableActiveTab === 1) && (
                        <Group gap="xs" mb="xs" align="center" wrap="wrap">
                            {hasAppliedSimple && (
                                <Badge
                                    size="lg"
                                    variant="light"
                                    color="blue"
                                    rightSection={
                                        <ActionIcon
                                            size="sm"
                                            variant="transparent"
                                            color="blue"
                                            onClick={handleClearAppliedSimpleSearch}
                                            aria-label="Clear applied search (reload table without search)"
                                            style={{ marginLeft: 2 }}
                                        >
                                            <IconX size={12} />
                                        </ActionIcon>
                                    }
                                    title={appliedExpressionDialectOmitted === undefined
                                        ? transformedSimpleBadgeTitle
                                        : makeRawAndEncodedTitle('search', simpleSearchLabel)}
                                >
                                    {appliedSimpleSearchType === 'simpleCustom'
                                        ? `custom: ${truncateForBadge(simpleSearchLabel)}`
                                        : (appliedExpressionDialectOmitted === undefined
                                            ? transformedSimpleBadgeText
                                            : `${appliedExpressionDialectOmitted ? 'search-freetext' : 'search-expression'}: ${truncateForBadge(simpleSearchLabel)}`)}
                                </Badge>
                            )}
                            {hasAppliedAdvanced && (
                                <Badge
                                    size="lg"
                                    variant="light"
                                    color="violet"
                                    rightSection={
                                        <ActionIcon
                                            size="sm"
                                            variant="transparent"
                                            color="violet"
                                            onClick={() => {
                                                setCustomFilterBadgeText(null);
                                                onClearAppliedFilter?.();
                                                updateUrlHashQueryParams({ search: appliedSearchQuery, filter: '', page: 1 }, 'push');
                                            }}
                                            aria-label="Clear applied filter (reload table without filter)"
                                            style={{ marginLeft: 2 }}
                                        >
                                            <IconX size={12} />
                                        </ActionIcon>
                                    }
                                    title={makeRawAndEncodedTitle('filter', appliedAdvancedFilter)}
                                >
                                    {(customFilterBadgeText && appliedAdvancedFilter?.trim() === customFilterBadgeText.trim())
                                        ? `custom: ${truncateForBadge(appliedAdvancedFilter.trim())}`
                                        : `Filter: ${truncateForBadge(appliedAdvancedFilter.trim())}`}
                                </Badge>
                            )}
                        </Group>
                    )}
                    {(tableActiveTab === 0 || tableActiveTab === 1) && (
                        <Group justify="flex-end" mb="xs" align="center">
                            {!hideTopPagination && !customDataRenderer && totalResults > 0 && (
                                <Box flex={1}>
                                    <CommonPagination
                                        currentPage={currentPage}
                                        totalPages={Math.ceil(totalResults / itemsPerPage)}
                                        totalResults={totalResults}
                                        onPageChange={handlePageChange}
                                        itemsPerPage={itemsPerPage}
                                    />
                                </Box>
                            )}
                            {onRefresh != null && (
                                <ActionIcon
                                    onClick={handleRefresh}
                                    disabled={isLoading || refreshCooldown}
                                    aria-label={refreshCooldown ? "Refresh (wait)" : "Refresh"}
                                    title={refreshCooldown ? "Refresh available in a few seconds" : "Refresh"}
                                    variant="subtle"
                                    size="md"
                                >
                                    <IconRefresh size={18} />
                                </ActionIcon>
                            )}
                            {!hideSearchExtras && (
                                <ActionIcon
                                    onClick={handleFullscreenToggle}
                                    aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                                    variant="subtle"
                                    size="md"
                                >
                                    {isFullscreen ? <IconMinimize size={18} /> : <IconMaximize size={18} />}
                                </ActionIcon>
                            )}
                            {!hideSearchExtras && bindingConstraintsMap && Object.keys(bindingConstraintsMap).length > 0 && (
                                <Tooltip label={showBindingWarnings ? "Hide binding warnings" : "Show binding warnings"}>
                                    <ActionIcon
                                        onClick={() => setShowBindingWarnings((v) => !v)}
                                        aria-label={showBindingWarnings ? "Hide binding warnings" : "Show binding warnings"}
                                        variant="subtle"
                                        size="md"
                                        color={showBindingWarnings ? 'yellow' : 'gray'}
                                    >
                                        <IconAlertTriangle size={18} />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </Group>
                    )}

                    {customDataRenderer ? (
                        customDataRenderer({
                            data,
                            isLoading,
                            currentPage,
                            totalResults,
                            itemsPerPage,
                            onPageChange: handlePageChange,
                            onRowClick: handleRowClick,
                        })
                    ) : (
                        <GenericTable
                            data={data}
                            columns={columns}
                            isLoading={isLoading}
                            onRowClick={handleRowClick}
                            getRowKey={getRowKey}
                            emptyMessage={emptyMessage || (
                                (simpleSearchQuery || appliedAdvancedFilter)
                                    ? `No ${getPluralizedLabel(entityType).toLowerCase()} matched your search. Try broadening your criteria.`
                                    : `No ${getSingularLabel(entityType).toLowerCase()} found`
                            )}
                            entityType={entityType}
                            selectable={selectable}
                            selectedItems={selectedItems}
                            onSelectionChange={onSelectionChange}
                            rowErrors={rowErrors}
                            onClearRowError={onClearRowError}
                            sortState={sortState}
                            onSortChange={onSortChange}
                            isFullscreen={isFullscreen}
                            listResponse={listResponse}
                            showEditIcon={showEditIcon}
                            onEditClick={onEditClick}
                            onTabChange={setTableActiveTab}
                            showBindingWarnings={showBindingWarnings}
                            bindingConstraintsMap={bindingConstraintsMap}
                            hideDebugTabs={hideDebugTabs}
                        />
                    )}

                    {!customDataRenderer && totalResults > 0 && (tableActiveTab === 0 || tableActiveTab === 1) && (
                        <Box mt="sm">
                            <CommonPagination
                                currentPage={currentPage}
                                totalPages={Math.ceil(totalResults / itemsPerPage)}
                                totalResults={totalResults}
                                onPageChange={handlePageChange}
                                itemsPerPage={itemsPerPage}
                            />
                        </Box>
                    )}
                </>
            ) : null}
        </Box>
    );
}
